import React from 'react';
import { Paintbrush, Hammer, Home, Wrench, BrickWall, HardHat, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/mockData';

const Services = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const services = [
    {
      icon: Paintbrush,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      iconBgColor: 'bg-blue-100',
      iconColor: 'text-blue-600',
      data: t.services.painting
    },
    {
      icon: Hammer,
      color: 'from-yellow-500 to-amber-600',
      bgColor: 'bg-amber-50',
      iconBgColor: 'bg-amber-100',
      iconColor: 'text-amber-600',
      data: t.services.carpentry
    },
    {
      icon: Home,
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-emerald-50',
      iconBgColor: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      data: t.services.remodeling
    },
    {
      icon: Wrench,
      color: 'from-slate-500 to-slate-600',
      bgColor: 'bg-slate-50',
      iconBgColor: 'bg-slate-100',
      iconColor: 'text-slate-600',
      data: t.services.maintenance
    },
    {
      icon: BrickWall,
      color: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-50',
      iconBgColor: 'bg-orange-100',
      iconColor: 'text-orange-600',
      data: t.services.masonry
    },
    {
      icon: HardHat,
      color: 'from-yellow-600 to-yellow-700',
      bgColor: 'bg-yellow-50',
      iconBgColor: 'bg-yellow-100',
      iconColor: 'text-yellow-700',
      data: t.services.construction
    }
  ];

  return (
    <section id="services" className="py-20 bg-gradient-to-b from-stone-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Arial, sans-serif' }}>
              {t.services.title}
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-6">
              {t.services.subtitle}
            </p>
            <div className="w-24 h-1 bg-yellow-600 mx-auto"></div>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card 
                  key={index} 
                  className={`${service.bgColor} border-none shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2`}
                >
                  <CardHeader>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className={`${service.iconBgColor} w-16 h-16 rounded-xl flex items-center justify-center`}>
                        <IconComponent className={`h-8 w-8 ${service.iconColor}`} />
                      </div>
                      <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Arial, sans-serif' }}>
                        {service.data.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {service.data.items.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center">
                              <Check className={`h-3 w-3 ${service.iconColor}`} />
                            </div>
                          </div>
                          <span className="text-slate-700 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-black to-zinc-900 rounded-2xl p-12 shadow-xl border border-yellow-600">
              <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Arial, sans-serif' }}>
                {language === 'en' 
                  ? "Don't see what you need?" 
                  : "¿No encuentras lo que necesitas?"}
              </h3>
              <p className="text-xl text-gray-300 mb-6">
                {language === 'en' 
                  ? "We offer custom solutions for unique projects. Contact us to discuss your needs." 
                  : "Ofrecemos soluciones personalizadas para proyectos únicos. Contáctanos para discutir tus necesidades."}
              </p>
              <button 
                onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-yellow-600 text-black px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-700 transition-colors duration-200 shadow-lg"
              >
                {language === 'en' ? 'Contact Us Today' : 'Contáctanos Hoy'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
